import { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { StorageKeys } from '@/lib/config';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isIOS(): boolean {
  const ua = navigator.userAgent || '';
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    nav.standalone === true
  );
}

/**
 * Home-screen install helper. Android/Chromium: waits for the native
 * `beforeinstallprompt` event and offers a one-tap Install button. iOS has no
 * install API, so Safari users get the manual Share -> Add to Home Screen
 * steps instead (nothing at all showed for them before). Hidden once running
 * standalone or after the driver dismisses it.
 */
export function InstallPrompt() {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [mode, setMode] = useState<'android' | 'ios' | null>(null);

  useEffect(() => {
    if (localStorage.getItem(StorageKeys.installPromptDismissed) === '1') return;
    if (isStandalone()) return;

    if (isIOS()) {
      setMode('ios');
      return;
    }
    const handler = (e: Event) => {
      e.preventDefault();
      setEvt(e as BeforeInstallPromptEvent);
      setMode('android');
    };
    window.addEventListener('beforeinstallprompt', handler as EventListener);
    return () => window.removeEventListener('beforeinstallprompt', handler as EventListener);
  }, []);

  if (!mode) return null;

  const dismiss = () => {
    localStorage.setItem(StorageKeys.installPromptDismissed, '1');
    setMode(null);
  };

  const install = async () => {
    if (!evt) return;
    await evt.prompt();
    const { outcome } = await evt.userChoice;
    if (outcome === 'accepted') dismiss();
    setMode(null);
  };

  return (
    <div className="bottom-4-safe fixed left-1/2 z-40 w-[min(92vw,26rem)] -translate-x-1/2 transform">
      <div className="card flex items-center gap-3 shadow-elevated">
        <Download className="h-5 w-5 shrink-0 text-brand-primaryBlue" />
        <div className="flex-1">
          <div className="font-semibold">Install Director Driver</div>
          {mode === 'android' ? (
            <p className="text-xs text-brand-muted">
              Install for faster startup and better GPS reliability.
            </p>
          ) : (
            <p className="text-xs text-brand-muted">
              In Safari: tap <Share aria-label="Share" className="inline h-3.5 w-3.5" /> Share,
              then <span className="font-semibold text-brand-text">Add to Home Screen</span>.
            </p>
          )}
        </div>
        {mode === 'android' && (
          <Button size="sm" variant="primary" onClick={install}>
            Install
          </Button>
        )}
        <button
          type="button"
          aria-label="Dismiss install prompt"
          onClick={dismiss}
          className="rounded p-1 text-brand-muted hover:bg-brand-surfaceAlt"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
